import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Search, Loader2, AlertTriangle, FileCheck2, FileX2, FileSearch } from 'lucide-react';
import { HistorialPanel } from './HistorialPanel';

const cleanNum = (v) => (v || '').replace(/\D/g, '');

const CBTE_TIPOS = [
  { v: '1', l: '01 - Factura A' }, { v: '2', l: '02 - Nota de Débito A' }, { v: '3', l: '03 - Nota de Crédito A' },
  { v: '6', l: '06 - Factura B' }, { v: '7', l: '07 - Nota de Débito B' }, { v: '8', l: '08 - Nota de Crédito B' },
  { v: '11', l: '11 - Factura C' }, { v: '12', l: '12 - Nota de Débito C' }, { v: '13', l: '13 - Nota de Crédito C' },
  { v: '51', l: '51 - Factura M' }, { v: '52', l: '52 - Nota de Débito M' }, { v: '53', l: '53 - Nota de Crédito M' },
  { v: '201', l: '201 - FCE MiPyME A' }, { v: '206', l: '206 - FCE MiPyME B' }, { v: '211', l: '211 - FCE MiPyME C' },
];
const DOC_TIPOS = [
  { v: 'none', l: '(sin especificar)' }, { v: '80', l: '80 - CUIT' }, { v: '86', l: '86 - CUIL' },
  { v: '96', l: '96 - DNI' }, { v: '99', l: '99 - Consumidor Final' },
];

const money = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  return Number.isNaN(n) ? String(v) : n.toLocaleString('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 2 });
};

const InfoRow = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-1.5 border-b border-slate-100 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="font-medium text-slate-800 text-right break-all">{value ?? '—'}</span>
  </div>
);

export default function ArcaWscdc() {
  const navigate = useNavigate();
  const [f, setF] = useState({
    cbte_modo: 'CAE', cuit_emisor: '', pto_vta: '', cbte_tipo: '1', cbte_nro: '',
    cbte_fch: '', imp_total: '', cod_autorizacion: '', doc_tipo_receptor: 'none', doc_nro_receptor: '',
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  const buscar = async (e) => {
    e?.preventDefault();
    if (cleanNum(f.cuit_emisor).length !== 11) { toast.error('CUIT del emisor: 11 dígitos'); return; }
    if (!f.pto_vta || !f.cbte_nro) { toast.error('Completá punto de venta y número'); return; }
    if (!f.cbte_fch) { toast.error('Ingresá la fecha del comprobante'); return; }
    if (!f.imp_total) { toast.error('Ingresá el importe total'); return; }
    if (!f.cod_autorizacion.trim()) { toast.error('Ingresá el CAE / código de autorización'); return; }

    setLoading(true); setError(''); setResult(null);
    try {
      const body = {
        cbte_modo: f.cbte_modo,
        cuit_emisor: cleanNum(f.cuit_emisor),
        pto_vta: Number(f.pto_vta),
        cbte_tipo: Number(f.cbte_tipo),
        cbte_nro: Number(f.cbte_nro),
        cbte_fch: (f.cbte_fch || '').replaceAll('-', ''), // YYYY-MM-DD -> YYYYMMDD
        imp_total: Number(f.imp_total),
        cod_autorizacion: f.cod_autorizacion.trim(),
        doc_tipo_receptor: f.doc_tipo_receptor === 'none' ? null : f.doc_tipo_receptor,
        doc_nro_receptor: f.doc_nro_receptor ? cleanNum(f.doc_nro_receptor) : null,
      };
      const res = await api.post('/arca/wscdc/constatar', body);
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error consultando WSCDC');
    } finally {
      setLoading(false);
    }
  };

  const r = result?.resumen || {};
  const aprobado = r.resultado ? String(r.resultado).toUpperCase() === 'A' : null;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="arca-wscdc-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><FileSearch size={22} /> Constatación de Comprobantes (WSCDC)</h1>
        <p className="text-slate-500 text-sm mt-1">Verificá si un comprobante electrónico es válido/autorizado en ARCA.</p>
      </div>

      <Tabs defaultValue="consulta">
        <TabsList data-testid="arca-wscdc-tabs">
          <TabsTrigger value="consulta" data-testid="arca-wscdc-tab-consulta">Consulta</TabsTrigger>
          <TabsTrigger value="historial" data-testid="arca-wscdc-tab-historial">Historial</TabsTrigger>
        </TabsList>

        <TabsContent value="consulta" className="space-y-6 mt-4">
          <Card className="p-5">
            <form onSubmit={buscar} className="grid md:grid-cols-3 gap-4">
              <div>
                <Label>Modalidad</Label>
                <Select value={f.cbte_modo} onValueChange={(v) => set('cbte_modo', v)}>
                  <SelectTrigger className="mt-1" data-testid="arca-wscdc-modo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CAE">CAE</SelectItem>
                    <SelectItem value="CAEA">CAEA</SelectItem>
                    <SelectItem value="CAI">CAI</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>CUIT emisor</Label>
                <Input data-testid="arca-wscdc-cuit-emisor" value={f.cuit_emisor} onChange={(e) => set('cuit_emisor', e.target.value)} className="font-mono mt-1" placeholder="30123456789" maxLength={13} />
              </div>
              <div>
                <Label>Tipo de comprobante</Label>
                <Select value={f.cbte_tipo} onValueChange={(v) => set('cbte_tipo', v)}>
                  <SelectTrigger className="mt-1" data-testid="arca-wscdc-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {CBTE_TIPOS.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Punto de venta</Label>
                <Input data-testid="arca-wscdc-ptovta" value={f.pto_vta} onChange={(e) => set('pto_vta', cleanNum(e.target.value))} className="font-mono mt-1" placeholder="1" />
              </div>
              <div>
                <Label>Número de comprobante</Label>
                <Input data-testid="arca-wscdc-nro" value={f.cbte_nro} onChange={(e) => set('cbte_nro', cleanNum(e.target.value))} className="font-mono mt-1" placeholder="123" />
              </div>
              <div>
                <Label>Fecha del comprobante</Label>
                <Input type="date" data-testid="arca-wscdc-fecha" value={f.cbte_fch} onChange={(e) => set('cbte_fch', e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Importe total</Label>
                <Input type="number" step="0.01" data-testid="arca-wscdc-importe" value={f.imp_total} onChange={(e) => set('imp_total', e.target.value)} className="font-mono mt-1" placeholder="12100.00" />
              </div>
              <div className="md:col-span-2">
                <Label>CAE / Código de autorización</Label>
                <Input data-testid="arca-wscdc-cae" value={f.cod_autorizacion} onChange={(e) => set('cod_autorizacion', e.target.value)} className="font-mono mt-1" placeholder="74123456789012" maxLength={14} />
              </div>
              <div>
                <Label>Tipo doc. receptor (opcional)</Label>
                <Select value={f.doc_tipo_receptor} onValueChange={(v) => set('doc_tipo_receptor', v)}>
                  <SelectTrigger className="mt-1" data-testid="arca-wscdc-doctipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DOC_TIPOS.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Nro. doc. receptor (opcional)</Label>
                <Input data-testid="arca-wscdc-docnro" value={f.doc_nro_receptor} onChange={(e) => set('doc_nro_receptor', e.target.value)} className="font-mono mt-1" placeholder="20123456789" />
              </div>
              <div className="md:col-span-3 flex justify-end">
                <Button type="submit" disabled={loading} data-testid="arca-wscdc-consultar-btn" className="bg-sky-600 hover:bg-sky-700">
                  {loading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Search size={16} className="mr-2" />}
                  {loading ? 'Constatando…' : 'Constatar'}
                </Button>
              </div>
            </form>
          </Card>

          {error && (
            <Card className="p-5 border-amber-200 bg-amber-50" data-testid="arca-wscdc-error">
              <div className="flex items-start gap-3 text-amber-800">
                <AlertTriangle size={18} className="mt-0.5" />
                <div>
                  <p className="font-medium">{error}</p>
                  {/certificado|clave|CUIT representada|adher/i.test(error) && (
                    <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate('/m/arca/configuracion')}>
                      Ir a Configuración
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          )}

          {result && (
            <Card className="p-6" data-testid="arca-wscdc-result">
              <div className={`flex items-center gap-3 p-4 rounded-lg ${aprobado ? 'bg-emerald-50 border border-emerald-200' : 'bg-rose-50 border border-rose-200'}`}>
                {aprobado ? <FileCheck2 size={28} className="text-emerald-600" /> : <FileX2 size={28} className="text-rose-600" />}
                <div>
                  <p className={`font-semibold ${aprobado ? 'text-emerald-800' : 'text-rose-800'}`}>
                    {aprobado ? 'Comprobante AUTORIZADO' : `Comprobante ${r.resultado === 'R' ? 'RECHAZADO' : (r.resultado || 'sin resultado')}`}
                  </p>
                  <p className="text-sm text-slate-500">Procesado: {r.fch_proceso || '—'}</p>
                </div>
                {result.cached && <Badge variant="outline" className="ml-auto text-slate-500">desde caché</Badge>}
              </div>

              {r.comprobante && (
                <div className="mt-4 grid md:grid-cols-2 gap-x-8">
                  <div>
                    <InfoRow label="CUIT emisor" value={r.comprobante.cuit_emisor} />
                    <InfoRow label="Punto de venta" value={r.comprobante.pto_vta} />
                    <InfoRow label="Tipo / Número" value={`${r.comprobante.cbte_tipo ?? '—'} / ${r.comprobante.cbte_nro ?? '—'}`} />
                  </div>
                  <div>
                    <InfoRow label="Fecha" value={r.comprobante.cbte_fch} />
                    <InfoRow label="Importe total" value={money(r.comprobante.imp_total)} />
                    <InfoRow label="CAE" value={r.comprobante.cod_autorizacion} />
                  </div>
                </div>
              )}

              {Array.isArray(r.observaciones) && r.observaciones.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-amber-700 mb-1">Observaciones</p>
                  <ul className="text-sm text-slate-600 list-disc pl-5 space-y-0.5">
                    {r.observaciones.map((o, i) => <li key={i}><span className="font-mono text-slate-400">[{o.code}]</span> {o.msg}</li>)}
                  </ul>
                </div>
              )}
              {Array.isArray(r.errores) && r.errores.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-rose-700 mb-1">Errores</p>
                  <ul className="text-sm text-rose-600 list-disc pl-5 space-y-0.5">
                    {r.errores.map((o, i) => <li key={i}><span className="font-mono">[{o.code}]</span> {o.msg}</li>)}
                  </ul>
                </div>
              )}
            </Card>
          )}
        </TabsContent>

        <TabsContent value="historial" className="mt-4">
          <HistorialPanel service="wscdc" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
